import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { IChecklistBenchmarkViewData } from '../checklists-benchmark/checklists-benchmark.component';
import {
    IChecklist2ReportBenchDataByCheckpoints,
    IChecklist2ReportDateRange,
    IChecklistV2ReportParameterFilter,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { FiChecklist2ReportHistoryService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-history.service';
import { FiChecklist2ReportHelperService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-helper.service';
import { ObjectTypes } from '@fi-shared/types';

interface IChecklistHistoryCheckpointsBenchmarkViewData {
    ChecklistName: string;
    ChecklistId: number;
    Checkpoints: IChecklistBenchmarkViewData[];
}

@Component({
    selector: 'fi-checklist-history-checkpoints-benchmark',
    templateUrl: './checkpoints-benchmark.component.html',
    styleUrls: ['./checkpoints-benchmark.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist-history-checkpoints-benchmark' },
    standalone: false,
})
export class FiChecklistHistoryCheckpointsBenchmarkComponent implements OnChanges {
    @Input({ required: true }) checklistHistoryDateRange: IChecklist2ReportDateRange;
    @Input({ required: true }) benchmarkFilter: IChecklistV2ReportParameterFilter;
    @Input({ required: true }) auditMode: boolean;
    @Output() onShowDetailedAnswersReport = new EventEmitter<IChecklistV2ReportParameterFilter>();

    benchmarkViewData: IChecklistHistoryCheckpointsBenchmarkViewData[] = [];

    constructor(
        private fiChecklist2ReportHistoryService: FiChecklist2ReportHistoryService,
        private cdr: ChangeDetectorRef,
        private fiChecklist2ReportHistoryHelperService: FiChecklist2ReportHelperService,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.checklistHistoryDateRange || changes.benchmarkFilter) this.loadBenchDataByCheckpoints();
    }

    private loadBenchDataByCheckpoints() {
        this.fiChecklist2ReportHistoryService
            .getBenchDataByCheckpoints(this.benchmarkFilter, this.checklistHistoryDateRange, this.auditMode)
            .then((result: IChecklist2ReportBenchDataByCheckpoints[]) => {
                this.benchmarkViewData = this.getBenchmarkViewData(result);
                this.cdr.markForCheck();
            });
    }

    getChecklistBenchmarkCardSizeClassName(): string {
        return this.fiChecklist2ReportHistoryHelperService.getChecklistBenchmarkCardSizeClassName(
            this.benchmarkFilter?.BusinessUnitIds?.length,
        );
    }

    getBenchmarkViewData(
        data: IChecklist2ReportBenchDataByCheckpoints[],
    ): IChecklistHistoryCheckpointsBenchmarkViewData[] {
        return data.map(
            (item: IChecklist2ReportBenchDataByCheckpoints): IChecklistHistoryCheckpointsBenchmarkViewData => {
                return {
                    ChecklistName: item.ChecklistName,
                    ChecklistId: item.ChecklistId,
                    Checkpoints: item.Checkpoints.map((cp) => {
                        return {
                            ObjectName: cp.CheckpointName,
                            ScheduleName: cp.ScheduleName,
                            ObjectId: cp.CheckpointId,
                            ScheduleId: cp.ScheduleId,
                            CreatedByUserId: cp.CheckpointCreatedByUserId,
                            CreatedByUserName: cp.CheckpointCreatedByUserName,
                            DueDateTime: cp.DueDateTime,
                            Units: cp.Units,
                            CheckpointsCount: 1,
                        };
                    }),
                };
            },
        );
    }

    getCheckpointCardHeading(item: IChecklistHistoryCheckpointsBenchmarkViewData): string {
        return item.Checkpoints?.length
            ? `${item.ChecklistName} - ${item.Checkpoints[0].ScheduleName}:`
            : item.ChecklistName;
    }

    showDetailedAnswersReport(
        businessUnitId: number,
        checklistId: number,
        benchmarkViewData: IChecklistBenchmarkViewData,
    ) {
        this.onShowDetailedAnswersReport.emit({
            BusinessUnitIds: [businessUnitId],
            ChecklistIds: [checklistId],
            ScheduleIds: [benchmarkViewData.ScheduleId],
            CheckpointIds: [benchmarkViewData.ObjectId],
        } as IChecklistV2ReportParameterFilter);
    }

    protected readonly ObjectTypes = ObjectTypes;
}
